/**
 * CMS: Our Energy Pledge Application
 */

// Import design tokens CSS (must be first for Vite to process correctly)
import '@ignite/web/themes/cms';

// Development mode shorthand
const DEV = import.meta.env.DEV;

// Measure FOUC timing (dev only)
const startTime = DEV ? performance.now() : null;

// Register FontAwesome icons used in the application
import { registerIcons, registerSvgs, xcelSwirl } from '@ignite/web';
import * as icons from './icons.js';

registerIcons(icons);
registerSvgs({ 'xcel-swirl': xcelSwirl });

// Import Xcel Energy Web Components (for custom element registration)
import * as components from './components.js';

// Import Pine Island infographic custom element
import './pine-island-infographic.js';

// Import GA4 tracking (initializes automatically)
import './ga4-tracking.js';

// Log when components are defined
Promise.all([components])
  .then(() => {
    if (DEV) {
      const endTime = performance.now();
      const upgradeTime = endTime - startTime;
      console.log(`⚡ Web components upgraded in ${upgradeTime.toFixed(2)}ms`);
    }
  });

// Initialize Pine Island infographic nodes
const PINE_ISLAND_NODES = [
  {
    id: 1, cx: 98.3, cy: 64.22,
    title: '1. Infrastructure Upgrades',
    body: 'Google, not customers, will pay for the infrastructure upgrades to serve the data center. These costs will not be spread to other customers.',
  },
  {
    id: 2, cx: 198.15, cy: 65.49,
    title: '2. Lowering Costs For Customers',
    body: 'Because adding a new, large customer helps spread out the cost of running the electric grid, it can help keep energy bills for all customers more affordable over time. Minnesota customers could see an average annual benefit of $75 million and a total of about $1.1 billion over the 15-year contract.',
  },
  {
    id: 3, cx: 288.76, cy: 101.79,
    title: '3. Clean Energy',
    body: 'Google will add 1,900 megawatts of new clean energy resources to our grid at no cost to existing customers.',
  },
  {
    id: 4, cx: 332.67, cy: 199.7,
    title: '4. Battery Investments',
    body: 'Google will invest $50 million in a program that is building out a battery storage network across Minnesota, benefitting all Minnesota customers by optimizing the energy grid and addressing rising electricity demand.',
  },
  {
    id: 5, cx: 262.19, cy: 271.27,
    title: '5. Jobs Created',
    body: 'The project is expected to provide 100 permanent operational jobs and hundreds of construction jobs, as well as create hundreds of indirect jobs, such as construction, skilled trade and other professional service positions.',
  },
  {
    id: 6, cx: 151.45, cy: 300.12,
    title: '6. STEM Education Support',
    body: 'Google is establishing a $24 million fund for STEM education at Pine Island Public Schools.',
  },
  {
    id: 7, cx: 79.88, cy: 246.06,
    title: '7. Water Usage',
    body: 'Google will use advanced, air-controlled cooling technology, which results in minimal water use for cooling.',
  },
  {
    id: 8, cx: 37.63, cy: 152.44,
    title: '8. Tax Revenue',
    body: 'The City of Pine Island, Goodhue County and the Pine Island School District will benefit from tens of millions of dollars in property tax revenue.',
  },
];

const initInfographic = () => {
  const infographic = document.querySelector('pine-island-infographic');
  if (infographic) infographic.nodes = PINE_ISLAND_NODES;
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInfographic);
} else {
  initInfographic();
}

// Scroll to hash on page load (if present)
if (window.location.hash) {
  const scrollToHash = () => {
    const target = document.querySelector(window.location.hash);
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scrollToHash);
  } else {
    scrollToHash();
  }
}

// Smooth scroll for anchor links
document.querySelectorAll('a[href^="#"]').forEach(anchor => {
  anchor.addEventListener('click', function (e) {
    e.preventDefault();
    const hash = this.getAttribute('href');
    const target = document.querySelector(hash);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.pushState(null, '', hash);
    }
  });
});

// Handle back/forward button navigation with hash changes
window.addEventListener('hashchange', () => {
  if (!window.location.hash) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } else {
    const target = document.querySelector(window.location.hash);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
});

// Initialize settings panel (non-production environments only)
const isNonProduction = DEV ||
  window.location.hostname.includes('.dev') ||
  window.location.hostname.includes('.qa') ||
  window.location.hostname.includes('localhost');

if (isNonProduction) {
  import('./non-prod/settingsPanel.js').then(({ createSettingsPanel }) => {
    createSettingsPanel();
    console.log('✓ App initialized');
  });
}
